<template>
  <div
    class="px-6 py-5 overflow-y-auto right-content-grid-vs h-[calc(100vh-309px)]"
  >
    <div class="w-full text-[#414141] font-sans">
      <h3 class="text-[1.25rem] leading-[1.75rem] mb-[1rem] font-semibold">Societal Change</h3>

      <div v-if="isLoading" class="flex justify-center items-center h-[200px]">
        <ActivityIndicator />
      </div>

      <!-- Core Program Tabs -->
      <div v-else class="mb-6">
        <div class="flex space-x-4 border-b border-gray-200">
          <button
            v-for="(program, index) in corePrograms"
            :key="program.id || index"
            @click="handleProgramChange(index)"
            :class="[
              'px-4 py-2 text-sm font-medium border-b-2 -mb-px',
              activeProgramIndex === index
                ? 'border-[#00423D] text-[#00423D]'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            ]"
          >
            {{ program.program_name || `Program ${index + 1}` }}
          </button>
        </div>
      </div>

      <!-- Societal Change Content for Active Program -->
      <div v-if="corePrograms.length > 0">
        <div class="mb-4">
          <h4 class="font-['Open_Sans'] font-semibold text-[18px] leading-[140%] text-[#414141] align-middle">
            {{ corePrograms[activeProgramIndex].program_name }}
          </h4>
        </div>

        <div class="w-full">
          <QuestionField
            v-model="programAnswers[activeProgramIndex]"
            :question="questionText"
            placeholder="Enter your program's societal change..."
            :error-message="errorMessage || (v$.currentAnswer.$error ? v$.currentAnswer.$errors[0]?.$message : '')"
          />
        </div>
      </div>
      <div v-else class="text-center py-8 text-gray-500">
        Please add core programs in the Core Annual Programs section first.
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import QuestionField from "@/components/QuestionField.vue";
import ActivityIndicator from "@/components/ActivityIndicator.vue";
import { useAuthStore } from "@/stores/auth";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import global from "@/global";
import { useStepValidation } from "@/composables/useStepValidation";

const isLoading = ref(true);
const questionLabel = ref("");
const questionText = ref("");
const errorMessage = ref("");
const activeProgramIndex = ref(0);
const corePrograms = ref([]);
const programAnswers = ref([]);
const lastSavedValues = ref([]);

// Validation rules
const rules = {
  currentAnswer: {
    required: helpers.withMessage("Answer is required!", required),
  }
};

const v$ = useVuelidate(rules, { currentAnswer: computed(() => programAnswers.value[activeProgramIndex.value] || '') });

// Fetching the auth store
const authStore = useAuthStore();

// Define categoryId and questionId as refs
const categoryId = ref("");
const questionId = ref("");

// Handle program change
const handleProgramChange = async (index) => {
  activeProgramIndex.value = index;
  // Only fetch the answer for the newly selected program
  await getUserAnswer(corePrograms.value[index].id);
};

// Custom validation function for the useStepValidation composable
const validateStep = async () => {
  // First trigger Vuelidate validation
  await v$.value.$validate();

  // If there are validation errors, immediately return false
  if (v$.value.$invalid) {
    return false;
  }

  try {
    // Save all program answers
    const success = await saveAllProgramAnswers();
    if (!success) {
      errorMessage.value = "Please ensure all answers are saved before proceeding.";
      return false;
    }
    return true;
  } catch (error) {
    console.error("Failed to save answers:", error);
    errorMessage.value = "Please ensure your answers are saved before proceeding.";
    return false;
  }
};

// Use the step validation composable
useStepValidation({
  initialValidState: false,
  validateFn: validateStep,
});

// Fetching question data directly using fetch API
async function fetchQuestionData() {
  try {
    const res = await fetch(`${global.API_URL}/questions/finalizing_your_budget_societal_change`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
    });

    if (!res.ok) {
      throw new Error(`HTTP error! Status: ${res.status}`);
    }

    const data = await res.json();
    questionLabel.value = data.question.label;
    questionText.value = data.question.title;
    categoryId.value = data.question.category_id;
    questionId.value = data.question.id;
  } catch (error) {
    errorMessage.value = "Failed to load question data. Please try again.";
    console.error(error);
  }
}

// Fetching user answer directly using fetch API
async function getUserAnswer(coreProgramId = null) {
  try {
    // If no coreProgramId is provided, fetch all programs
    if (!coreProgramId) {
      // Initialize arrays if they're empty
      if (programAnswers.value.length === 0) {
        programAnswers.value = corePrograms.value.map(() => "");
        lastSavedValues.value = corePrograms.value.map(() => "");
      }

      // Fetch answers for all programs
      await Promise.all(corePrograms.value.map(async (program, index) => {
        const res = await fetch(
          `${global.API_URL}/answers/core_program/${program.id}?question_id=${questionId.value}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${authStore.user.accessToken}`,
            },
          }
        );

        if (!res.ok) {
          throw new Error(`HTTP error! Status: ${res.status}`);
        }

        const data = await res.json();
        if (data.answer?.answer) {
          programAnswers.value[index] = data.answer.answer;
          lastSavedValues.value[index] = data.answer.answer;
        }
      }));
    } else {
      // Fetch answer for specific program
      const programIndex = corePrograms.value.findIndex(p => p.id === coreProgramId);
      if (programIndex === -1) return;

      const res = await fetch(
        `${global.API_URL}/answers/core_program/${coreProgramId}?question_id=${questionId.value}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${authStore.user.accessToken}`,
          },
        }
      );

      if (!res.ok) {
        throw new Error(`HTTP error! Status: ${res.status}`);
      }

      const data = await res.json();
      if (data.answer?.answer) {
        programAnswers.value[programIndex] = data.answer.answer;
        lastSavedValues.value[programIndex] = data.answer.answer;
      }
    }
  } catch (error) {
    console.error("Failed to fetch user answers:", error);
    errorMessage.value = "Failed to load your previous answers. You can still create new ones.";
  }
}

// Add a function to save all program answers
async function saveAllProgramAnswers() {
  try {
    // Save answers for all programs that have changed
    const savePromises = corePrograms.value.map(async (program, index) => {
      if (programAnswers.value[index] !== lastSavedValues.value[index]) {
        const res = await fetch(`${global.API_URL}/answers/core_program/${program.id}`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${authStore.user.accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            category_id: categoryId.value,
            question_id: questionId.value,
            answer: programAnswers.value[index],
          }),
        });

        if (!res.ok) {
          throw new Error(`HTTP error! Status: ${res.status}`);
        }

        const data = await res.json();
        if (data.status) {
          lastSavedValues.value[index] = programAnswers.value[index];
        } else {
          throw new Error("Failed to save answer");
        }
      }
    });

    await Promise.all(savePromises);
    return true;
  } catch (error) {
    console.error("Failed to save all program answers:", error);
    return false;
  }
}

onMounted(async () => {
  try {
    isLoading.value = true;
    errorMessage.value = "";

    // First ensure we have the latest user data with organization ID
    await authStore.fetchUserData();

    if (!authStore.user?.organization_id) {
      errorMessage.value = "Unable to load organization data. Please try refreshing the page.";
      return;
    }

    // Fetch core programs
    const programsResponse = await fetch(`${global.API_URL}/core-programs`, {
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
    });
    const programsData = await programsResponse.json();
    corePrograms.value = programsData.corePrograms || [];

    // Initialize arrays with empty strings
    programAnswers.value = corePrograms.value.map(() => "");
    lastSavedValues.value = corePrograms.value.map(() => "");

    // Then load the question data
    await fetchQuestionData();

    if (!questionId.value) {
      errorMessage.value = "Unable to load question data. Please try refreshing the page.";
      return;
    }

    // Finally fetch all program answers
    await getUserAnswer();

  } catch (error) {
    console.error("Error during initialization:", error);
    errorMessage.value = "An error occurred while loading the data. Please try refreshing the page.";
  } finally {
    isLoading.value = false;
  }
});
</script>

<style scoped>
.page-title {
  font-weight: 600;
  font-size: 20px;
  line-height: 140%;
  color: #414141;
  margin-bottom: 24px;
}
</style> 